// 1b: the line that came off the real catalog and has no price yet.
//
// WHAT THIS IS FOR. anixify.product carries no price -- deliberately and
// permanently, because every price in this system is computed by the engine
// from the six inputs on a quote line. So a product picked from the real
// master arrives with no listPrice and no netPrice, and there is nothing to
// send the engine until someone types one.
//
// ⚠️ IT DESCRIBES A GAP IN THE QUOTE, NOT A FAILED REQUEST, which is why it
// is not the price-error strip and not SaveQuoteBanner. Nothing went wrong:
// the catalog behaved exactly as designed and the app declined to invent a
// number. The remedy is data entry, and the message says so.
//
// ⚠️ DERIVED, NOT LATCHED -- same as FreightBasisBanner and for the same
// reason. Typing a List Price fixes the condition, and the banner has to go
// away by itself when it does. QuoteDetail recomputes the problems on every
// render rather than setting a flag, so "it clears when fixed" is a property
// of the structure instead of something a later edit has to remember.
//
// ⚠️ WARNING TONE, NOT DESTRUCTIVE. An unpriced line is an ordinary stage of
// building a quote -- you pick the parts, then you price them. Red would say
// something broke. What is genuinely blocked (the save) is stated in words
// rather than by colour.

import { AlertTriangle } from "lucide-react";
import {
  semanticColors, radius, spacing, typography, iconSize, iconStroke,
} from "@/styles/tokens";

export function UnpricedBanner({
  // The one sentence, already built. Passed in rather than derived here so
  // this banner and both save seams cannot word the same refusal differently
  // -- unpricedMessage in services/unpricedGuard.ts owns it.
  message,
}: {
  message: string;
}) {
  if (!message) return null;

  return (
    <div
      data-testid="quote-unpriced-banner"
      className="flex items-start"
      style={{
        gap: spacing[8],
        marginTop: spacing[12],
        padding: `${spacing[8]}px ${spacing[12]}px`,
        backgroundColor: semanticColors.warningBg,
        border: `1px solid ${semanticColors.warningBorder}`,
        borderRadius: radius.base,
        fontSize: typography.fontSize.sm,
        color: semanticColors.warningTextOnTint,
      }}
    >
      <AlertTriangle
        size={iconSize.md}
        strokeWidth={iconStroke.regular}
        style={{ flexShrink: 0, marginTop: spacing[2], color: semanticColors.warning }}
      />
      <span>
        {/* It says the quote is not being saved, because it is not: both save
            paths decline while this holds, for the same reason the pricing
            request skips these lines. A line quoted at Rs 0 because nobody
            had typed a price is worse than a save that did not happen. */}
        <strong>This quote is not being saved.</strong>{" "}
        <span data-testid="quote-unpriced-detail">{message}</span>
      </span>
    </div>
  );
}
